import React, { useState, useCallback, useEffect } from 'react';
import LicenseAccordion from './license_details_accordion';
import { Grid, TextField, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import getbusinessuuidApi from '../../../../services/managelicense/index';
import LicenseModal from '../manage_license/license_details_accordion/licensemodal';
import EditImageModal from './license_details_accordion/editimage/index';
import { setEncryptedCookie, getDecryptedCookie, removeCookie } from '../../../../utils';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const useStyles = makeStyles({
    animatedButton: {
        background: 'linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)',
        color: '#fff',
        padding: '10px 30px',
        fontSize: '16px',
        fontWeight: 'bold',
        textTransform: 'none',
        borderRadius: '5px',
        transition: 'transform 0.3s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            background: 'linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)'
        }
    }
});

const initialValues = { number: '', name: '', licensealiasname: '' };

const validationSchema = Yup.object().shape({
    number: Yup.string()
        .matches(
            /^(?=.*\d).{3,12}$/,
            'License number must be between 3 and 12 characters and contain at least one number'
        )
        .min(3, 'License number must be at least 3 characters')
        .max(12, 'License number must be at most 12 characters')
        .required('License number is required'),

    name: Yup.string()
        .min(3, 'License name must be at least 3 characters')
        .max(35, 'License name must not exceed 35 characters')
        .matches(/^[^0-9]*$/, 'License name must not contain numbers')
        .required('License name is required')
});

const ManageLicense = ({
    onPrevious,
    businesssid,
    fileId,
    onNext,
    setLanguageType,
    languageType,
    loanNumberType,
    setLoanNumberType
}) => {
    useEffect(() => {
        setEncryptedCookie('lll', languageType);
    }, []);
    const menuItems = useSelector((state) => state.menuReducer.items);
    const createMenuItem = menuItems?.find((item) => item.name.toLowerCase() === 'subscriptions');
    const isDisabled = createMenuItem?.access_type === 'read_write';
    const organizationItems = useSelector((state) => state.organizationReducer.items);
    const classes = useStyles();
    const [licensebusinesssid, setLicenseBusinesssid] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [licenseToAdd, setLicenseToAdd] = useState(null);
    const [isFormFilled, setIsFormFilled] = useState(false);
    const [businessid, setBusinessid] = useState();
    const { productid, busUid, businessuuid } = useParams();
    const proid = getDecryptedCookie('proid') || productid;

    const handleAccordionChange = (index) => (event, isExpanded) => {
        setExpanded(isExpanded ? index : null);
    };

    const handleConfirmAdd = (newLicense) => {
        setLicenseBusinesssid((prevLicenses) => [
            ...prevLicenses,
            {
                license_number: newLicense.number,
                license_name: newLicense.name,
                alias: '',
                opening_number: '',
                starting_date: '',
                date_from: null,
                date_to: null
            }
        ]);
        setLicenseToAdd(null);
    };

    useEffect(() => {
        setBusinessid(getDecryptedCookie('bi') || busUid);
    }, [organizationItems]);

    const getLicensebusinessId = useCallback((proid, businessid) => {
        const onSuccess = (res) => {
            if (res.data.status) {
                setLicenseBusinesssid(res.data.data.data);
            }
        };

        const onFailure = (err) => {
            console.error('Failed to fetch data', err);
        };

        getbusinessuuidApi.getLicensebusinessId(proid, businessid).then(onSuccess).catch(onFailure);
    }, []);

    useEffect(() => {
        getLicensebusinessId(proid, businessid);
    }, [getLicensebusinessId, businessid, organizationItems]);

    return (
        <>
            {isDisabled && (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, { resetForm }) => {
                        setLicenseToAdd({
                            number: values.number,
                            name: values.name,
                            alias: ''
                        });

                        resetForm();
                    }}
                >
                    {({ handleSubmit, touched, errors, values, handleChange }) => (
                        <Form onSubmit={handleSubmit}>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name='number'
                                        label='License Number'
                                        fullWidth
                                        helperText={touched.number && errors.number ? errors.number : ''}
                                        error={Boolean(touched.number && errors.number)}
                                        onChange={handleChange}
                                        value={values.number}
                                        InputProps={{
                                            style: {
                                                borderColor: touched.number && errors.number ? 'red' : 'black'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name='name'
                                        label='License Name'
                                        fullWidth
                                        helperText={touched.name && errors.name ? errors.name : ''}
                                        error={Boolean(touched.name && errors.name)}
                                        onChange={handleChange}
                                        value={values.name}
                                        InputProps={{
                                            style: {
                                                borderColor: touched.name && errors.name ? 'red' : 'black'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button type='submit' variant='contained' fullWidth style={{ marginTop: '8px' }}>
                                        Add
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            )}

            <LicenseAccordion
                licenses={licensebusinesssid.map((license) => ({
                    number: license.license_number,
                    name: license.license_name,
                    loan_exists: license.loan_exists,
                    bill_file_id: license.bill_file_id
                }))}
                expanded={expanded}
                setExpanded={setExpanded}
                handleAccordionChange={handleAccordionChange}
                setLicenses={setLicenseBusinesssid}
                businesssid={businessid}
                licensebusinesssid={licensebusinesssid}
                id={businessid}
                getLicensebusinessId={getLicensebusinessId}
                fileId={fileId}
                deatils={businesssid}
                languageType={languageType}
                setLanguageType={setLanguageType}
            />

            <LicenseModal
                open={Boolean(licenseToAdd)}
                license={licenseToAdd}
                onClose={() => setLicenseToAdd(null)}
                onConfirm={() => handleConfirmAdd(licenseToAdd)}
                getLicensebusinessId={getLicensebusinessId}
                businesssid={businesssid}
                loanNumberType={loanNumberType}
                setLoanNumberType={setLoanNumberType}
            />

            <EditImageModal
                licenses={Array.isArray(licensebusinesssid) ? licensebusinesssid : []}
                expanded={expanded}
                handleAccordionChange={handleAccordionChange}
                setLicenses={setLicenseBusinesssid}
                businesssid={businessid}
                licensebusinesssid={licensebusinesssid}
                fileId={fileId}
            />

            <Grid container justifyContent='space-between' sx={{ mt: 2 }}>
                <Grid item>
                    <Button variant='outlined' style={{ marginTop: '8px' }} onClick={onPrevious}>
                        Previous
                    </Button>
                </Grid>

                <Grid item>
                    <Button variant='outlined' sx={{ flexGrow: 0 }} style={{ marginTop: '8px' }} onClick={onNext}>
                        NEXT
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};

export default ManageLicense;
